import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { meetingService } from '@services';

// Extraction runs in the background on the server; poll while it is pending.
const POLL_MS = 4000;

const isProcessing = (meeting) => meeting?.status === 'PROCESSING' && !meeting?.stale;

export const useMeetingAIStatus = () => {
  return useQuery({
    queryKey: ['meetingAIStatus'],
    queryFn: () => meetingService.getStatus(),
    staleTime: 60 * 1000,
  });
};

export const useMeetings = (projectId) => {
  return useQuery({
    queryKey: ['meetings', projectId],
    queryFn: () => meetingService.getAll(projectId),
    enabled: !!projectId,
    refetchInterval: (query) => (query.state.data?.data?.some(isProcessing) ? POLL_MS : false),
  });
};

export const useMeeting = (id) => {
  return useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingService.getById(id),
    enabled: !!id,
    refetchInterval: (query) => (isProcessing(query.state.data?.data) ? POLL_MS : false),
  });
};

const useMeetingMutation = (mutationFn, { invalidateTasks = false } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meeting'] });
      if (invalidateTasks) queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUploadMeeting = () => useMeetingMutation(meetingService.upload);

export const useRetryMeeting = () => useMeetingMutation(meetingService.retry);

export const useDeleteMeeting = () => useMeetingMutation(meetingService.delete);

export const useApproveDrafts = () =>
  useMeetingMutation(({ meetingId, draftIds }) => meetingService.approve(meetingId, draftIds), {
    invalidateTasks: true,
  });

export const useUpdateDraft = () =>
  useMeetingMutation(({ draftId, data }) => meetingService.updateDraft(draftId, data));

export const useDiscardDraft = () => useMeetingMutation(meetingService.discardDraft);

export const useRestoreDraft = () => useMeetingMutation(meetingService.restoreDraft);
